import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import LeadCaptureCard from '../sharedComponents/LeadCaptureCard';
import { readGarage, onGarageChange } from '../../utils/garage';

const API_BASE = (process.env.REACT_APP_LEADS_API_BASE || 'https://api.marketcast.co').replace(/\/$/, '');

const GIcon = ({ children }) => (
  <span className="fi-gic">
    <svg viewBox="0 0 24 24" fill="none" stroke="#111" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{children}</svg>
  </span>
);

const money = (n) => `$${Math.round(n).toLocaleString('en-US')}`;

const quoteLine = (q) => {
  const parts = [`Last check: ${q.flagged} junk ${q.flagged === 1 ? 'line' : 'lines'} flagged`];
  if (q.high > 0) parts.push(`about ${money(q.low)} to ${money(q.high)} to push back on`);
  if (q.deadCredit) parts.push('plus a dead federal credit line');
  return `${parts.join(', ')}.`;
};

function Garage() {
  const [g, setG] = useState(() => readGarage());
  useEffect(() => onGarageChange(() => setG(readGarage())), []);

  const lastQuote = g.quotes[g.quotes.length - 1];
  const empty = !g.shortlist.length && !lastQuote && !g.state;

  return (
    <div className="fi-box" data-track-context="home_garage">
      <div className="fi-box-h">
        <span>Your garage</span>
        <span>{g.updatedAt ? `Updated ${new Date(g.updatedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : 'Saved on this device'}</span>
      </div>
      <div className="fi-box-b">
        <div className="fi-gi">
          <GIcon><path d="M5 17h14v-5l-2-5H7l-2 5z" /><circle cx="8" cy="17" r="2" /><circle cx="16" cy="17" r="2" /></GIcon>
          <div>
            <b>Shortlist</b>
            <span>{g.shortlist.length ? g.shortlist.map((c) => c.name).join(', ') : 'Save cars from Find your EV and they land here.'}</span>
          </div>
          <RouterLink className="fi-btn fi-btn--sm" to={g.shortlist.length >= 2 ? '/compare' : '/find-your-ev'}>
            {g.shortlist.length >= 2 ? 'Compare' : 'Find EVs'}
          </RouterLink>
        </div>
        <div className="fi-gi">
          <GIcon><path d="M6 2h12v20l-3-2-3 2-3-2-3 2z" /><path d="M9 7h6M9 11h6" /></GIcon>
          <div>
            <b>Quote checks</b>
            <span>{lastQuote ? quoteLine(lastQuote) : 'Run a dealer quote through the Deal-Checker.'}</span>
          </div>
          <RouterLink className="fi-btn fi-btn--sm" to="/deal-checker">{lastQuote ? 'Check another' : 'Check one'}</RouterLink>
        </div>
        <div className="fi-gi">
          <GIcon><path d="M12 21s-7-6.2-7-11a7 7 0 0 1 14 0c0 4.8-7 11-7 11z" /><circle cx="12" cy="10" r="2.5" /></GIcon>
          <div>
            <b>Your state</b>
            <span>{g.state ? `${g.state.name}: incentives, fees and charging costs.` : 'Pick your state to see what it still pays.'}</span>
          </div>
          <RouterLink className="fi-btn fi-btn--sm" to={g.state ? `/incentives/${g.state.slug}` : '/incentives'}>{g.state ? 'Open' : 'Pick'}</RouterLink>
        </div>
        <p style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>
          {empty ? 'No account, nothing to sign up for. It just remembers what you looked at, on this device.' : 'Saved on this device only. Nothing leaves your browser.'}
        </p>
      </div>
    </div>
  );
}

// Live teaser of the latest weekly brief; hides itself when there isn't one.
function BriefTeaser() {
  const [items, setItems] = useState(undefined);
  useEffect(() => {
    let alive = true;
    fetch(`${API_BASE}/api/evs/brief/latest`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (alive) setItems(d && d.brief && d.brief.items ? d.brief.items.slice(0, 3) : null); })
      .catch(() => { if (alive) setItems(null); });
    return () => { alive = false; };
  }, []);
  if (!items || items.length === 0) return null;
  return (
    <div className="fi-box" style={{ marginTop: 32 }} data-track-context="home_brief_teaser">
      <div className="fi-box-h"><span>This week in EVs</span><span>Every Monday</span></div>
      <div className="fi-box-b">
        {items.map((it, i) => (
          <a key={i} href={it.url} target="_blank" rel="noopener noreferrer" style={{ color: '#111', fontWeight: 700, lineHeight: 1.4 }}>{it.headline}</a>
        ))}
        <RouterLink className="fi-btn fi-btn--sm" to="/brief" style={{ alignSelf: 'flex-start' }}>Read the full brief</RouterLink>
      </div>
    </div>
  );
}

export default function KitSection() {
  return (
    <section className="fi-kit" id="kit" aria-labelledby="fi-kit-h">
      <div className="fi-wrap">
        <h2 className="fi-h2" id="fi-kit-h">Your garage</h2>
        <p className="fi-sub">Pick up where you left off, and walk into the dealership knowing more than the finance guy.</p>
        <div className="fi-kit-g">
          <Garage />
          <div className="fi-coupon" data-track-context="home_kit_coupon">
            <span className="fi-scis" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="#111" strokeWidth="2" strokeLinecap="round"><circle cx="6" cy="6" r="3" /><circle cx="6" cy="18" r="3" /><path d="M20 4L8.1 15.9M14.5 14.5L20 20M8.1 8.1L12 12" /></svg>
            </span>
            <span className="fi-tag">Clip &amp; save · Free</span>
            <h3>The Dealership Survival Kit</h3>
            <p>Every junk fee to refuse and what to actually pay. Four printable pages, free.</p>
            <LeadCaptureCard
              compact
              title=""
              blurb=""
              buttonLabel="Get the free PDF"
              leadType="survival_kit"
              collectZip
              downloadUrl="/survival-kit.pdf"
              downloadLabel="Download the kit (PDF)"
              successTitle="Here's your kit."
              successBody="Your download should start automatically, tap the button if it doesn't."
            />
          </div>
        </div>
        <BriefTeaser />
      </div>
    </section>
  );
}
